/**
 * Spec 200 second-pass UX redesign — illustrated empty state.
 *
 * Replaces the bare "icon + one-liner" empty placeholders with the
 * 4-element pattern NN/g recommends: illustration / title /
 * description / CTA. The illustration is a monoline SVG drawn
 * inline (no asset fetch, no extra round-trip) using `currentColor`
 * so it inherits the brand-aware token the parent passes through
 * the wrapper class.
 *
 * Variants:
 *   - `noWorks`  — empty WorkList ("Aún no hay trabajos").
 *   - `noResults` — search filter returned nothing.
 *   - `noTerminals` — Settings → admin sees no active web sessions.
 *
 * Usage:
 *   <EmptyState
 *     variant="noWorks"
 *     title="Aún no hay trabajos"
 *     body={<>Crea uno con <kbd>N</kbd> o desde el móvil.</>}
 *     action={{ label: 'Crear trabajo', onClick: () => navigate('/works/new') }}
 *   />
 *
 * Drop-in: every empty container in routes/* sources its copy
 * from i18n if/when we ship locales — for now copy is hard-coded
 * by the caller (matches the rest of the route surface).
 */
import { Plus } from 'lucide-react';
import type { ReactNode } from 'react';

import './EmptyState.css';

type Variant = 'noWorks' | 'noResults' | 'noTerminals';

interface EmptyStateProps {
  variant: Variant;
  title: string;
  body?: ReactNode;
  action?: {
    label: string;
    onClick: () => void;
    icon?: ReactNode;
    testId?: string;
  };
  testId?: string;
}

export function EmptyState({ variant, title, body, action, testId }: EmptyStateProps) {
  return (
    <div className={`empty empty--${variant}`} data-testid={testId} role="status">
      <div className="empty-illustration" aria-hidden="true">
        {variant === 'noWorks' && <NoWorksIllustration />}
        {variant === 'noResults' && <NoResultsIllustration />}
        {variant === 'noTerminals' && <NoTerminalsIllustration />}
      </div>
      <h2 className="empty-title">{title}</h2>
      {body && <p className="empty-body">{body}</p>}
      {action && (
        <button
          type="button"
          className="empty-cta"
          onClick={action.onClick}
          data-testid={action.testId}
        >
          {action.icon ?? <Plus size={16} strokeWidth={2} aria-hidden="true" />}
          {action.label}
        </button>
      )}
    </div>
  );
}

/* ── Illustrations ───────────────────────────────────────────────
   Monoline SVG, 140×140 viewBox. `stroke="currentColor"` so the
   wrapper's color token paints them. No fills — just outlines, the
   Notion / Linear empty-state aesthetic. */

function NoWorksIllustration() {
  return (
    <svg viewBox="0 0 140 140" width="140" height="140" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {/* Briefcase outline */}
      <rect x="22" y="44" width="96" height="62" rx="6" />
      <path d="M52 44V34a4 4 0 0 1 4-4h28a4 4 0 0 1 4 4v10" />
      <path d="M22 70h96" opacity="0.4" />
      {/* Sparkle accents to suggest "fresh, ready to fill" */}
      <path d="M34 24l2 4 4 2-4 2-2 4-2-4-4-2 4-2z" opacity="0.55" />
      <path d="M114 90l1.5 3 3 1.5-3 1.5-1.5 3-1.5-3-3-1.5 3-1.5z" opacity="0.55" />
    </svg>
  );
}

function NoResultsIllustration() {
  return (
    <svg viewBox="0 0 140 140" width="140" height="140" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {/* Magnifier */}
      <circle cx="58" cy="58" r="28" />
      <path d="M78 78l24 24" />
      {/* Empty inside — three dashes suggesting "no rows" */}
      <path d="M44 54h28" opacity="0.45" strokeDasharray="3 5" />
      <path d="M44 62h22" opacity="0.45" strokeDasharray="3 5" />
      <path d="M44 70h28" opacity="0.45" strokeDasharray="3 5" />
    </svg>
  );
}

function NoTerminalsIllustration() {
  return (
    <svg viewBox="0 0 140 140" width="140" height="140" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {/* Laptop screen */}
      <rect x="22" y="36" width="96" height="58" rx="4" />
      <path d="M14 102h112" />
      {/* Globe inside the screen — paired session metaphor */}
      <circle cx="70" cy="64" r="14" opacity="0.7" />
      <path d="M56 64h28" opacity="0.5" />
      <path d="M70 50a18 18 0 0 1 0 28a18 18 0 0 1 0-28z" opacity="0.5" />
    </svg>
  );
}
